/**
 * Utility functions for time-based booking mode
 * These functions support the flexible booking mode where customers can choose
 * specific times (e.g., 18:15, 18:30) instead of fixed time slots.
 */

import { format } from "date-fns";

export type BookingMode = 'slot' | 'time';
export type TimeLimitType = 'none' | 'reservations' | 'people';

export interface TimeSlotRange {
  start: string; // HH:mm or HH:mm:ss format
  end: string;   // HH:mm or HH:mm:ss format
  availableDays?: number[];
  durationOverrideMinutes?: number | null; // Per-slot duration override
}

export interface BookingModeSettings {
  bookingMode: BookingMode;
  bookingIntervalMinutes: 15 | 30 | 60;
  reservationDurationMinutes: number;
  timeLimitType: TimeLimitType;
  maxReservationsPerTime: number | null;
  maxPeoplePerTime: number | null;
}

/**
 * Parse a time string (HH:mm or HH:mm:ss) to total minutes since midnight
 */
export function parseTimeToMinutes(time: string): number {
  const parts = time.split(':').map(Number);
  return parts[0] * 60 + parts[1];
}

/**
 * Convert minutes since midnight to HH:mm format
 */
export function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

/**
 * Normalize time to HH:mm format (remove seconds if present)
 */
export function normalizeTimeFormat(time: string): string {
  const parts = time.split(':');
  return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
}

/**
 * ---- Services qui franchissent minuit (lot A) ----
 * Convention : un créneau dont end < start se termine le lendemain.
 * Pour tout créneau où end >= start, ces fonctions renvoient exactement
 * les mêmes valeurs que l'arithmétique historique.
 */
export const MINUTES_PER_DAY = 1440;

export function slotCrossesMidnight(slot: { start: string; end: string }): boolean {
  return parseTimeToMinutes(slot.end) < parseTimeToMinutes(slot.start);
}

/** Fin du créneau en minutes de service (peut dépasser 1440). */
export function slotEndMinutes(slot: { start: string; end: string }): number {
  const end = parseTimeToMinutes(slot.end);
  return slotCrossesMidnight(slot) ? end + MINUTES_PER_DAY : end;
}

/**
 * Position d'une heure dans le service d'un créneau (minutes depuis minuit
 * du jour de service). Une heure avant le début d'un créneau qui franchit
 * minuit appartient au lendemain : +1440.
 */
export function serviceMinutes(time: string, slot: { start: string; end: string }): number {
  const t = parseTimeToMinutes(time);
  if (slotCrossesMidnight(slot) && t < parseTimeToMinutes(slot.start)) {
    return t + MINUTES_PER_DAY;
  }
  return t;
}

/** Horloge HH:mm à partir de minutes de service (repliées sur 24h). */
export function serviceMinutesToClock(minutes: number): string {
  return minutesToTime(((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY);
}

/**
 * Chevauchement entre deux réservations situées sur des dates de service
 * potentiellement différentes. dayOffset = écart en jours par rapport à une
 * date de référence (ex. -1 pour une réservation de la veille).
 * startMinutes doit être exprimé en minutes de service.
 */
export function doServiceRangesOverlap(
  a: { dayOffset: number; startMinutes: number; durationMinutes: number },
  b: { dayOffset: number; startMinutes: number; durationMinutes: number }
): boolean {
  const aStart = a.dayOffset * MINUTES_PER_DAY + a.startMinutes;
  const bStart = b.dayOffset * MINUTES_PER_DAY + b.startMinutes;
  return aStart < bStart + b.durationMinutes && bStart < aStart + a.durationMinutes;
}

/**
 * Generate available booking times within time slots based on interval
 * @param timeSlots - Array of active time slots for the day
 * @param intervalMinutes - Booking interval (15, 30, or 60 minutes)
 * @param reservationDurationMinutes - How long a reservation occupies a table
 * @returns Array of bookable times in HH:mm format
 */
export function generateAvailableTimes(
  timeSlots: TimeSlotRange[],
  intervalMinutes: 15 | 30 | 60,
  reservationDurationMinutes: number = 120
): string[] {
  const times: string[] = [];
  const seen = new Set<string>();
  const order = new Map<string, number>();

  for (const slot of timeSlots) {
    const startMinutes = parseTimeToMinutes(slot.start);
    const endMinutes = slotEndMinutes(slot);
    
    // Use per-slot duration override if available, otherwise fall back to global
    const effectiveDuration = slot.durationOverrideMinutes ?? reservationDurationMinutes;
    
    // Generate times at each interval, but only if there's enough time
    // for the reservation duration before the slot ends
    let current = startMinutes;
    
    // The last possible booking time is: slot end - reservation duration
    // But we also want to allow booking at least at the slot start
    const lastPossibleBooking = Math.max(startMinutes, endMinutes - effectiveDuration);
    
    while (current <= lastPossibleBooking) {
      const timeStr = serviceMinutesToClock(current);
      if (!seen.has(timeStr)) {
        times.push(timeStr);
        seen.add(timeStr);
        order.set(timeStr, current);
      }
      current += intervalMinutes;
    }
  }

  // Tri chronologique dans le service (00:30 après 23:30 si le service franchit minuit)
  return times.sort((a, b) => order.get(a)! - order.get(b)!);
}

/**
 * Check if a specific time falls within any of the given time slots
 */
export function isTimeInSlot(time: string, slots: TimeSlotRange[]): boolean {

  return slots.some(slot => {
    const startMinutes = parseTimeToMinutes(slot.start);
    const endMinutes = slotEndMinutes(slot);
    const t = serviceMinutes(time, slot);
    return t >= startMinutes && t < endMinutes;
  });
}

/**
 * Find the slot that contains a given time
 */
export function findSlotContainingTime(time: string, slots: TimeSlotRange[]): TimeSlotRange | undefined {

  return slots.find(slot => {
    const startMinutes = parseTimeToMinutes(slot.start);
    const endMinutes = slotEndMinutes(slot);
    const t = serviceMinutes(time, slot);
    return t >= startMinutes && t < endMinutes;
  });
}

/**
 * Check if two time-based reservations overlap
 * Used in TIME mode to determine if a table is blocked
 * 
 * @param time1 - Start time of first reservation (HH:mm)
 * @param duration1 - Duration of first reservation in minutes
 * @param time2 - Start time of second reservation (HH:mm)  
 * @param duration2 - Duration of second reservation in minutes
 * @returns true if the reservations overlap
 */
export function doTimesOverlap(
  time1: string,
  duration1: number,
  time2: string,
  duration2: number
): boolean {
  const start1 = parseTimeToMinutes(time1);
  const end1 = start1 + duration1;
  const start2 = parseTimeToMinutes(time2);
  const end2 = start2 + duration2;
  
  // Two ranges overlap if one starts before the other ends
  return start1 < end2 && start2 < end1;
}

/**
 * Check if a proposed reservation time conflicts with an existing reservation
 * considering reservation duration in TIME mode
 */
export function hasTimeConflict(
  proposedTime: string,
  proposedDuration: number,
  existingTime: string,
  existingDuration: number
): boolean {
  return doTimesOverlap(proposedTime, proposedDuration, existingTime, existingDuration);
}

/**
 * Validate that a booking time respects the configured interval
 * e.g., if interval is 30, valid times are :00 and :30
 */
export function isValidBookingInterval(time: string, intervalMinutes: 15 | 30 | 60): boolean {
  const minutes = parseTimeToMinutes(time);
  const minutePart = minutes % 60;
  
  switch (intervalMinutes) {
    case 60:
      return minutePart === 0; // Only :00
    case 30:
      return minutePart === 0 || minutePart === 30; // :00 or :30
    case 15:
      return minutePart % 15 === 0; // :00, :15, :30, :45
    default:
      return false;
  }
}

/**
 * Get the end time of a reservation given its start time and duration
 */
export function getReservationEndTime(startTime: string, durationMinutes: number): string {
  const startMinutes = parseTimeToMinutes(startTime);
  const endMinutes = startMinutes + durationMinutes;
  return minutesToTime(endMinutes);
}

/**
 * Format booking mode settings for display
 */
export function formatBookingModeDescription(settings: BookingModeSettings): string {
  if (settings.bookingMode === 'slot') {
    return 'Réservation par créneaux horaires';
  }
  
  const intervalLabel = settings.bookingIntervalMinutes === 60 
    ? '1 heure'
    : `${settings.bookingIntervalMinutes} minutes`;
  
  return `Réservation toutes les ${intervalLabel}`;
}

/**
 * Generate a preview of available booking times for display in settings
 */
export function generateTimePreview(intervalMinutes: 15 | 30 | 60, baseHour: number = 18): string[] {
  const times: string[] = [];
  const baseMinutes = baseHour * 60;
  
  for (let i = 0; i < 4; i++) {
    times.push(minutesToTime(baseMinutes + (i * intervalMinutes)));
  }
  
  return times;
}

/**
 * Count the number of reservations for each time slot
 * Used to enforce max_reservations_per_time limit in TIME booking mode
 * 
 * @param reservations - All reservations for the day
 * @param selectedDate - The selected booking date
 * @returns Map<string, number> where key is time (HH:mm) and value is count
 */
export function countReservationsByTime(
  reservations: Array<{ date: string | Date; startTime: string; status: string }>,
  selectedDate: Date
): Map<string, number> {
  const dateStr = format(selectedDate, "yyyy-MM-dd");
  const counts = new Map<string, number>();
  
  reservations
    .filter(r => {
      const resDate = typeof r.date === 'string' ? r.date : format(r.date, "yyyy-MM-dd");
      return resDate === dateStr && r.status !== "annulé" && r.status !== "en_attente";
    })
    .forEach(r => {
      const time = normalizeTimeFormat(r.startTime);
      counts.set(time, (counts.get(time) || 0) + 1);
    });
  
  return counts;
}

/**
 * Count the total number of people for each time slot
 * Used to enforce max_people_per_time limit in TIME booking mode
 * 
 * @param reservations - All reservations for the day (with numberOfPeople)
 * @param selectedDate - The selected booking date
 * @returns Map<string, number> where key is time (HH:mm) and value is total people count
 */
export function countPeopleByTime(
  reservations: Array<{ 
    date: string | Date; 
    startTime: string; 
    status: string;
    numberOfPeople: number;
    numberOfChildren?: number;
  }>,
  selectedDate: Date
): Map<string, number> {
  const dateStr = format(selectedDate, "yyyy-MM-dd");
  const counts = new Map<string, number>();
  
  reservations
    .filter(r => {
      const resDate = typeof r.date === 'string' ? r.date : format(r.date, "yyyy-MM-dd");
      return resDate === dateStr && r.status !== "annulé" && r.status !== "en_attente";
    })
    .forEach(r => {
      const time = normalizeTimeFormat(r.startTime);
      counts.set(time, (counts.get(time) || 0) + r.numberOfPeople + (r.numberOfChildren || 0));
    });
  
  return counts;
}
